import React, { useState, useEffect } from 'react';
import { DEFAULT_TIME_BLOCKS, SESSION_TYPES } from '../../constants/competitionConstants';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormHelperText,
  Paper,
  Checkbox,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { AVAILABLE_LEVELS } from '../../constants/competitionConstants';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { parseISO, format, parse } from 'date-fns';
import { parseTime } from '../../utils/dateUtils';

const SessionDefinition = ({
  initialData,
  onSave,
  onCancel,
  onDelete,
  editMode = false,
  availableLevels = [],
  availableRotations = [],
  sessionDate // Prop for initial date (will be stored as startDate)
}) => {
  // Debug: Log what we receive
  // console.log('SessionDefinition initialData received:', initialData);
  // console.log('SessionDefinition initialData.timeBlocks:', initialData?.timeBlocks);
  // console.log('SessionDefinition sessionDate:', sessionDate);

  const normalizeSessionDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return format(value, 'yyyy-MM-dd');
    if (typeof value === 'string') return value.split(' ')[0];
    return null;
  };

  const initialStartDate = normalizeSessionDate(sessionDate) ||
    normalizeSessionDate(initialData?.startDate);

  // Normalize timeBlocks to handle case variations (warmupStart vs warmUpStart)
  const normalizeTimeBlocks = (timeBlocks) => {
    if (!timeBlocks) return {};

    const normalized = {};
    Object.entries(timeBlocks).forEach(([key, value]) => {
      const normalizedValue = (() => {
        if (typeof value !== 'string') return value;
        const trimmed = value.trim();
        const dateMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})\s+(.+)$/);
        if (!dateMatch) return value;
        const timeMatches = trimmed.match(/\b\d{1,2}:\d{2}:\d{2}\b/g);
        if (!timeMatches || timeMatches.length === 0) return value;
        const timePart = timeMatches[timeMatches.length - 1];
        return `${dateMatch[1]} ${timePart}`;
      })();

      // Find matching DEFAULT_TIME_BLOCK with more flexible matching
      const matchingBlock = DEFAULT_TIME_BLOCKS.find(b => {
        const canonicalLower = b.id.toLowerCase();
        const keyLower = key.toLowerCase();
        
        // Direct match (case-insensitive)
        if (canonicalLower === keyLower) return true;
        
        // Check for alternative formats (camelCase vs other variations)
        // For example: competitionStart vs competitionstart vs competition_start
        if (canonicalLower.replace(/_/g, '').replace(/-/g, '') === 
            keyLower.replace(/_/g, '').replace(/-/g, '')) return true;
        
        return false;
      });

      if (matchingBlock) {
        // Use the canonical key from DEFAULT_TIME_BLOCKS
        // Only exclude undefined values, allow empty strings and other falsey values
        if (normalizedValue !== undefined) {
          normalized[matchingBlock.id] = normalizedValue;
        }
      } else {
        // For debugging - log unrecognized keys
        // console.log(`No matching DEFAULT_TIME_BLOCK found for key: ${key}`);
      }
    });

    return normalized;
  };

  // Infer gender from rotation if not explicitly set
  const inferGenderFromRotation = () => {
    if (initialData?.gender) return initialData.gender;

    // Check if rotation name indicates gender
    const rotation = availableRotations.find(r => r.id === initialData?.rotationId);
    if (rotation?.name?.toLowerCase().includes('men')) return 'M';
    if (rotation?.name?.toLowerCase().includes('women')) return 'F';

    // Default to F
    return 'F';
  };

  // Normalize level values (e.g., "L5" -> "Level 5")
  const normalizeLevel = (level) => {
    if (!level) return level;
    // Match patterns like "L5", "L 5", "l5" and convert to "Level 5"
    const match = level.match(/^L\s*(\d+)$/i);
    if (match) {
      return `Level ${match[1]}`;
    }
    return level;
  };

  const normalizedTimeBlocks = normalizeTimeBlocks(initialData?.timeBlocks);
  // console.log('Normalized timeBlocks:', normalizedTimeBlocks);

  const [sessionData, setSessionData] = useState({
    sessionNumber: initialData?.sessionNumber || '',
    name: initialData?.name || '',
    description: initialData?.description || '',
    levels: (initialData?.levels || []).map(l => normalizeLevel(l)),
    gender: inferGenderFromRotation(),
    type: initialData?.type || 'Traditional',
    numFlights: initialData?.numFlights || 1,
    timeBlocks: normalizedTimeBlocks,
    enabledTimeBlocks: initialData?.enabledTimeBlocks ||
      (editMode && normalizedTimeBlocks
        ? Object.keys(normalizedTimeBlocks)
        : editMode ? DEFAULT_TIME_BLOCKS.map(b => b.id) : []),
    rotationId: initialData?.rotationId || '',
    startDate: initialStartDate || null
  });

  // Local state for time picker values to prevent clearing during typing
  const [localTimeValues, setLocalTimeValues] = useState({});

  const getAvailableLevels = () => {
    let levels = sessionData.gender === 'M'
      ? AVAILABLE_LEVELS.filter(level => !level.value.toLowerCase().includes('xcel'))
      : [...AVAILABLE_LEVELS];

    // Include any existing session levels that aren't in the standard list
    // (so they can be deselected)
    const existingLevelValues = new Set(levels.map(l => l.value));
    (sessionData.levels || []).forEach(existingLevel => {
      if (!existingLevelValues.has(existingLevel)) {
        levels.push({ value: existingLevel, label: existingLevel });
      }
    });

    return levels;
  };

  const handleGenderChange = (event) => {
    const newGender = event.target.value;
    setSessionData(prev => ({
      ...prev,
      gender: newGender,
      levels: [] // Clear levels when gender changes
    }));
  };

  const [validationErrors, setValidationErrors] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    // This is just for debugging, remove it later
    // eslint-disable-next-line no-console
    // console.log('SessionDefinition sessionData state:', sessionData);
  }, [sessionData]);

  const validateSession = (data) => {
    const errors = {};
    if (!data.levels || data.levels.length === 0) {
      errors.levels = 'At least one level must be selected';
    }
    if (!data.sessionNumber) {
      errors.sessionNumber = 'Session number is required';
    }
    return errors;
  };

  const handleChange = (field) => (event) => {
    setSessionData({
      ...sessionData,
      [field]: event.target.value
    });
    // Clear validation error for this field if it exists
    if (validationErrors[field]) {
      setValidationErrors({
        ...validationErrors,
        [field]: null
      });
    }
  };


  const handleTimeChange = (field) => (newValue) => {
    // Update local state immediately to prevent clearing
    setLocalTimeValues(prev => ({
      ...prev,
      [field]: newValue
    }));

    // Only update session data when we have a valid complete date
    if (newValue && !isNaN(newValue.getTime())) {
      // Convert to ISO format with fixed date (session date) and time
      const timeString = format(newValue, "HH:mm:ss");
      const baseDate = sessionData.startDate || format(newValue, 'yyyy-MM-dd');
      const newDateTimeString = `${baseDate} ${timeString}`;

      // console.log(`Time change for ${field}:`, newDateTimeString);

      setSessionData(prev => ({
        ...prev,
        timeBlocks: {
          ...prev.timeBlocks,
          [field]: newDateTimeString
        }
      }));
    }
  };

  const handleTimeAccept = (field) => (newValue) => {
    // Update session data on accept
    if (newValue && !isNaN(newValue.getTime())) {
      // Convert to ISO format with fixed date (session date) and time
      const timeString = format(newValue, "HH:mm:ss");
      const baseDate = sessionData.startDate || format(newValue, 'yyyy-MM-dd');
      const newDateTimeString = `${baseDate} ${timeString}`;

      setSessionData(prev => ({
        ...prev,
        timeBlocks: {
          ...prev.timeBlocks,
          [field]: newDateTimeString
        }
      }));
    }
  };

  const handleSessionDateChange = (newValue) => {
    if (newValue && !isNaN(newValue.getTime())) {
      const newDateString = format(newValue, 'yyyy-MM-dd');

      // Update session date and also update all existing time blocks to use the new date
      setSessionData(prev => {
        const updatedTimeBlocks = {};
        Object.entries(prev.timeBlocks).forEach(([key, value]) => {
          if (value) {
            // Extract time part and combine with new date
            const timePart = value.split(' ')[1] || '00:00:00';
            updatedTimeBlocks[key] = `${newDateString} ${timePart}`;
          }
        });

        return {
          ...prev,
          startDate: newDateString,
          timeBlocks: updatedTimeBlocks
        };
      });
    }
  };

  const handleSave = () => {
    const errors = validateSession(sessionData);
    if (Object.keys(errors).length === 0) {
      onSave(sessionData);
    } else {
      setValidationErrors(errors);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false);
    onDelete?.(initialData);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  return (
    <Paper sx={{ p: 3 }} data-ui="form-session-definition">
      <Typography variant="h6" gutterBottom>
        {editMode ? 'Edit Session' : 'Define Session'}
      </Typography>

      <Box sx={{ display: 'grid', gap: 2.5, mt: 2 }}>
        {/* Basic Info Section */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '120px 80px 1fr', gap: 2, alignItems: 'start' }}>
          <FormControl size="small">
            <InputLabel id="gender-label">Gender</InputLabel>
            <Select
              data-ui="form-session-def-gender"
              labelId="gender-label"
              id="gender"
              label="Gender"
              value={sessionData.gender}
              onChange={handleGenderChange}
            >
              <MenuItem value="F">Women</MenuItem>
              <MenuItem value="M">Men</MenuItem>
            </Select>
          </FormControl>

          <TextField
            data-ui="form-session-def-number"
            label="Session #"
            value={sessionData.sessionNumber}
            onChange={handleChange('sessionNumber')}
            error={!!validationErrors.sessionNumber}
            helperText={validationErrors.sessionNumber}
            type="number"
            size="small"
          />

          <TextField
            data-ui="form-session-def-name"
            label="Session Name"
            value={sessionData.name}
            onChange={handleChange('name')}
            error={!!validationErrors.name}
            helperText={validationErrors.name}
            size="small"
          />
        </Box>

        <TextField
          data-ui="form-session-def-description"
          label="Session Description"
          value={sessionData.description}
          onChange={handleChange('description')}
          error={!!validationErrors.description}
          helperText={validationErrors.description || "This is the main title displayed for the session"}
          size="small"
        />

        {/* Session Type and Flights */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 2 }}>
          <FormControl size="small">
            <InputLabel id="type-label">Session Type</InputLabel>
            <Select
              data-ui="form-session-def-type"
              labelId="type-label"
              id="type"
              label="Session Type"
              value={sessionData.type}
              onChange={handleChange('type')}
            >
              {SESSION_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            data-ui="form-session-def-flights"
            label="# Flights"
            value={sessionData.numFlights}
            onChange={handleChange('numFlights')}
            type="number"
            size="small"
            inputProps={{ min: 1, max: 10 }}
          />
        </Box>

        {/* Levels and Rotation Section */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <FormControl error={!!validationErrors.levels} size="small">
            <InputLabel id="levels-label">Levels</InputLabel>
            <Select
              data-ui="form-session-def-levels"
              multiple
              labelId="levels-label"
              id="levels"
              label="Levels"
              value={sessionData.levels || []}
              onChange={handleChange('levels')}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {getAvailableLevels().map((level) => (
                <MenuItem key={level.value} value={level.value}>
                  <Checkbox
                    checked={sessionData.levels.indexOf(level.value) > -1}
                    sx={{ py: 0 }}
                  />
                  <ListItemText primary={level.label} />
                </MenuItem>
              ))}
            </Select>
            {validationErrors.levels && (
              <FormHelperText>{validationErrors.levels}</FormHelperText>
            )}
          </FormControl>

          <FormControl size="small">
            <InputLabel id="rotation-label">Rotation Method</InputLabel>
            <Select
              data-ui="form-session-def-rotation"
              labelId="rotation-label"
              id="rotation"
              value={sessionData.rotationId || ''}
              onChange={handleChange('rotationId')}
              label="Rotation Method"
            >
              <MenuItem value="">
                <em>No Rotation</em>
              </MenuItem>
              {availableRotations.map((rotation) => (
                <MenuItem key={rotation.id} value={rotation.id}>
                  {rotation.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Session Date */}
        <DatePicker
          data-ui="form-session-def-date"
          label="Session Date"
          value={sessionData.startDate ? parse(sessionData.startDate, 'yyyy-MM-dd', new Date()) : null}
          onChange={handleSessionDateChange}
          slotProps={{
            textField: {
              fullWidth: true,
              size: 'small',
              InputLabelProps: { shrink: true }
            }
          }}
        />

        {/* Time Blocks */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gridTemplateRows: 'repeat(3, auto)', gridAutoFlow: 'column', gap: 2 }}>
          {DEFAULT_TIME_BLOCKS.map((block) => (
            (editMode || sessionData.enabledTimeBlocks.includes(block.id)) && (
              <TimePicker
                data-ui={`form-session-def-time-${block.id}`}
                key={block.id}
                label={block.label}
                value={localTimeValues[block.id] !== undefined
                  ? localTimeValues[block.id]
                  : parseTime(sessionData.timeBlocks[block.id])}
                onChange={handleTimeChange(block.id)}
                onAccept={handleTimeAccept(block.id)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    InputLabelProps: { shrink: true },
                    inputProps: { placeholder: '' }
                  }
                }}
              />
            )
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
          {editMode && onDelete && (
            <Button
              data-ui="btn-session-def-delete"
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteClick}
              sx={{ mr: 'auto' }}
            >
              Delete Session
            </Button>
          )}
          <Button data-ui="btn-session-def-cancel" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            data-ui="btn-session-def-save"
            variant="contained"
            color="primary"
            onClick={handleSave}
          >
            {editMode ? 'Save Changes' : 'Create Session'}
          </Button>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        data-ui="dialog-delete-session"
      >
        <DialogTitle>Delete Session?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete Session {initialData?.sessionNumber}
            {initialData?.name ? ` (${initialData.name})` : ''}?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            data-ui="btn-confirm-delete-session"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default SessionDefinition;
